import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Button, Chip, IconButton, Typography } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { Controller } from 'react-hook-form'
import HHAutocompleteField from 'components/form-fields/v5/HHAutocompleteField'
import ContactOption from './ContactOption'
import T from 'T'
import { isEmail } from 'utils/validations'

const EmailRecipients = ({
  control,
  errors,
  contacts,
  toRecipients,
  ccRecipients,
  onToChange,
  onCcAdd,
  onCcRemove,
  onValidationChange,
}) => {
  const [ccInput, setCcInput] = useState('')
  const [showCc, setShowCc] = useState(false)
  const [ccError, setCcError] = useState('')
  const [toInputValue, setToInputValue] = useState('')

  const handleToRecipientsChange = (event, newValue) => {
    const processedRecipients = []
    let hasInvalidEmail = false

    newValue.forEach(item => {
      if (typeof item === 'string') {
        const email = item.trim()
        if (isEmail(email)) {
          processedRecipients.push({
            id: `manual-to-${crypto.randomUUID()}`,
            firstName: '',
            lastName: '',
            email,
            isManual: true,
          })
        } else if (email) {
          hasInvalidEmail = true
          processedRecipients.push({
            id: `invalid-to-${crypto.randomUUID()}`,
            firstName: '',
            lastName: '',
            email,
            isManual: true,
            isInvalid: true,
          })
        }
      } else {
        processedRecipients.push(item)
      }
    })

    onToChange(processedRecipients)

    if (onValidationChange) {
      onValidationChange(hasInvalidEmail)
    }
  }

  const handleAddCcEmail = () => {
    const email = ccInput.trim()
    if (!email) return

    if (!isEmail(email)) {
      setCcError(T.ENTER_VALID_EMAIL)
      return
    }

    if (toRecipients.includes(email)) {
      setCcError(T.EMAIL_ALREADY_ADDED)
      return
    }

    const manualContact = {
      id: `manual-cc-${Date.now()}`,
      firstName: '',
      lastName: '',
      email,
      isManual: true,
    }

    onCcAdd(manualContact)
    setCcInput('')
    setCcError('')
  }

  const handleCcKeyPress = e => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddCcEmail()
    }
  }

  const handleCcInputChange = e => {
    const newValue = e.target.value
    const options = newValue.split(/[,\s]+/)

    if (options.length > 1) {
      const emails = options.map(x => x.trim()).filter(x => x)
      emails.forEach(email => {
        if (isEmail(email)) {
          const allEmails = [...toRecipients.map(c => c.email), ...ccRecipients.map(c => c.email)]
          if (!allEmails.includes(email)) {
            const manualContact = {
              id: `manual-cc-${crypto.randomUUID()}`,
              firstName: '',
              lastName: '',
              email,
              isManual: true,
            }
            onCcAdd(manualContact)
          }
        }
      })
      setCcInput('')
      setCcError('')
    } else {
      setCcInput(newValue)
      if (ccError) {
        setCcError('')
      }
    }
  }

  const handleShowCC = () => {
    setShowCc(prev => !prev)
  }

  // Filter out already selected contacts from dropdown options
  const selectedEmails = new Set([...toRecipients.map(r => r.email), ...ccRecipients.map(r => r.email)])
  const availableContacts = contacts.filter(contact => !selectedEmails.has(contact.email))

  const handleToInputChange = (event, newInputValue) => {
    const options = newInputValue.split(/[,\s]+/)

    if (options.length > 1) {
      const newEmails = options.map(x => x.trim()).filter(x => x)
      handleToRecipientsChange(event, [...toRecipients, ...newEmails])
      setToInputValue('')
    } else {
      setToInputValue(newInputValue)
    }
  }

  const handleToBlur = event => {
    const email = toInputValue.trim()
    if (email && isEmail(email)) {
      handleToRecipientsChange(event, [...toRecipients, email])
      setToInputValue('')
    }
  }

  const handleToChange = (event, newValue) => {
    handleToRecipientsChange(event, newValue)
  }

  const getOptionLabel = option => {
    if (typeof option === 'string') return option
    return option.email || ''
  }

  const renderContactOption = (props, option) => <ContactOption props={props} option={option} />

  return (
    <>
      <Box mb={2} display="flex" alignItems="center" gap={2}>
        <Box flexGrow="1">
          <Controller
            name="toRecipients"
            control={control}
            rules={{
              validate: value => {
                if (value.length === 0) {
                  return T.AT_LEAST_ONE_RECIPIENT_REQUIRED
                }
                const hasInvalidEmail = value.some(recipient => recipient.isInvalid)
                if (hasInvalidEmail) {
                  return T.INVALID_EMAIL_ADDRESSES
                }
                return true
              },
            }}
            render={({ field }) => (
              <HHAutocompleteField
                sx={{
                  width: '100%',
                  '& .MuiAutocomplete-inputRoot': {
                    flexWrap: 'wrap',
                    height: 'auto !important',
                    minHeight: '40px',
                  },
                }}
                multiple
                freeSolo
                options={availableContacts}
                value={field.value}
                inputValue={toInputValue}
                onInputChange={handleToInputChange}
                onBlur={handleToBlur}
                onChange={handleToChange}
                getOptionLabel={getOptionLabel}
                renderOption={renderContactOption}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id || option}
                      label={typeof option === 'string' ? option : option.email}
                      size="small"
                    />
                  ))
                }
                label="To"
                placeholder={T.ADD_RECIPIENTS}
                deprecatedLabel={false}
                error={!!errors.toRecipients}
                helperText={errors.toRecipients?.message}
              />
            )}
          />
        </Box>
        <Button variant="text" onClick={handleShowCC}>
          {T.CC}
        </Button>
      </Box>

      {showCc && (
        <Box mb={2}>
          <Typography variant="body2" color="text.secondary" mb={0.5}>
            {T.CC}
          </Typography>
          <Box
            sx={{
              border: '1px solid',
              borderColor: ccError ? 'error.main' : 'divider',
              borderRadius: 1,
              p: 1,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 0.5,
              minHeight: 40,
              '&:focus-within': {
                borderColor: 'primary.main',
                borderWidth: 2,
              },
            }}
          >
            {ccRecipients.map(recipient => (
              <Chip key={recipient.id} label={recipient.email} size="small" onDelete={() => onCcRemove(recipient.id)} />
            ))}
            <Box
              component="input"
              sx={{
                border: 'none',
                outline: 'none',
                flex: 1,
                minWidth: 150,
                fontSize: 14,
                backgroundColor: 'transparent',
                '&::placeholder': {
                  color: 'text.secondary',
                },
              }}
              placeholder={ccRecipients.length === 0 ? T.ADD_CC_EMAIL : ''}
              value={ccInput}
              onChange={handleCcInputChange}
              onKeyPress={handleCcKeyPress}
              onBlur={handleAddCcEmail}
            />
            {ccInput && (
              <IconButton size="small" onClick={handleAddCcEmail}>
                <CheckCircleIcon fontSize="small" color="primary" />
              </IconButton>
            )}
          </Box>
          {ccError && <Box sx={{ color: 'error.main', fontSize: 12, mt: 0.5, ml: 1.5 }}>{ccError}</Box>}
        </Box>
      )}
    </>
  )
}

EmailRecipients.propTypes = {
  control: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  contacts: PropTypes.array.isRequired,
  toRecipients: PropTypes.array.isRequired,
  ccRecipients: PropTypes.array.isRequired,
  onToChange: PropTypes.func.isRequired,
  onCcAdd: PropTypes.func.isRequired,
  onCcRemove: PropTypes.func.isRequired,
  onValidationChange: PropTypes.func,
}

export default EmailRecipients
